import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** CREU 26-10: el cajón está en modo «hoja corta» del móvil. */
  #sheet = false;

  /**
   * CREU 26-10: al añadir, el tema repinta toda la sección de la cabecera (donde
   * vive el cajón) y el diálogo vuelve a la clase del servidor, sin .creu-sheet.
   * Mientras la hoja esté activa y abierta, se le vuelve a poner.
   */
  #sheetObserver = new MutationObserver(() => {
    const dialog = this.refs?.dialog;
    if (this.#sheet && dialog?.open && !dialog.classList.contains('creu-sheet')) {
      dialog.classList.add('creu-sheet');
    }
  });

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#sheetObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#sheetObserver.disconnect();
  }

  /**
   * CREU 26-10 (Alba): en móvil, al añadir desde la web sale una hoja corta desde
   * abajo (lo añadido, envío, esenciales y pagar), con la página detrás, en vez
   * del carrito a pantalla completa. El modo se decide solo si el cajón estaba
   * cerrado: con el cajón abierto este mismo evento llega por cambios de
   * cantidad o por añadir un esencial, y no hay que cambiar de modo.
   * Los estilos de la hoja están en snippets/cart-drawer.liquid (.creu-sheet).
   */
  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;
    this.openAfterAdd();
  };

  /**
   * CREU 29-10 (Alba): lo mismo que al añadir desde la ficha, pero público para
   * los añadidos propios (el «+» de las tarjetas, assets/creu-quick-add.js, y las
   * combinaciones). Antes esos abrían el carrito completo en vez de la hoja corta.
   */
  openAfterAdd() {
    const dialog = this.refs?.dialog;
    if (dialog && !dialog.open) {
      this.#sheet = window.matchMedia('(max-width: 749px)').matches;
      dialog.classList.toggle('creu-sheet', this.#sheet);
    }

    this.showDialog();
    if (this.#sheet) {
      this.#scrollSheetToTop();
    } else {
      this.#scrollToNewest();
    }
  }

  /**
   * CREU 26-10: la hoja se lee de arriba abajo (lo añadido primero). Tras el
   * repintado, otros scripts bajan la vista a la última línea; aquí se deja
   * arriba durante el primer segundo.
   */
  #scrollSheetToTop() {
    let intentos = 0;
    const subir = () => {
      const inner = this.refs?.dialog?.querySelector('.cart-drawer__inner');
      if (inner) inner.scrollTop = 0;
      if (++intentos < 8) setTimeout(subir, 150);
    };
    requestAnimationFrame(subir);
  }

  /**
   * CREU (revisión 29.07 de Alba): "los más recientes se añaden abajo y la vista
   * se posiciona abajo, con scroll hacia arriba para ver los anteriores".
   *
   * Shopify ya devuelve las líneas por orden de adición, así que lo nuevo queda
   * al final; lo que falta es dejar la vista ahí. El contenido del cajón se
   * repinta DESPUÉS del evento de añadir, por eso no vale con hacerlo una vez:
   * se reintenta unas cuantas veces hasta que el alto deja de cambiar.
   */
  #scrollToNewest() {
    let intentos = 0;
    let altoPrevio = -1;

    const bajar = () => {
      const dialog = this.refs?.dialog ?? this.querySelector('dialog');
      if (!dialog) return;

      // Quién scrollea de verdad depende del tema: aquí es un <scroll-hint
      // class="cart-drawer__items">, no el diálogo ni un div con nombre
      // previsible. Por eso se busca por comportamiento (overflow + contenido
      // que no cabe) en vez de por una lista de selectores.
      const scrollable = [...dialog.querySelectorAll('*'), dialog].find((el) => {
        if (el.scrollHeight <= el.clientHeight + 4) return false;
        const ov = getComputedStyle(el).overflowY;
        return ov === 'auto' || ov === 'scroll';
      });
      if (scrollable) {
        scrollable.scrollTop = scrollable.scrollHeight;
        if (scrollable.scrollHeight === altoPrevio) return; // ya está estable
        altoPrevio = scrollable.scrollHeight;
      }

      if (++intentos < 8) setTimeout(bajar, 120);
    };

    requestAnimationFrame(bajar);
  }

  open() {
    // CREU 26-10: abrir desde el icono es siempre el carrito completo.
    this.#sheet = false;
    this.refs?.dialog?.classList.remove('creu-sheet');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  /**
   * CREU 26-10: «Ver carrito» de la hoja corta del móvil → carrito completo,
   * sin cerrar y volver a abrir.
   */
  expand() {
    this.#sheet = false;
    this.refs?.dialog?.classList.remove('creu-sheet');
    this.#scrollToNewest();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
